import {
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
  StartAuthenticationOptions,
  StartRegistrationOptions,
  WebAuthnBrowserModule,
} from './webauthn.types'

/**
 * Lazy access to `@simplewebauthn/browser`.
 *
 * The library is never statically imported: it is a browser-only dependency that
 * the native mobile apps embedding drrweb-lib do not use, and a static import would
 * force them to install, bundle, and resolve it. Loading it through `import()` keeps
 * it in its own chunk, fetched the first time the passkey flow is actually triggered,
 * and lets a host app that does not ship the dependency fail gracefully — bundlers
 * emit a module stub that rejects at runtime rather than breaking the build.
 */

/**
 * Thrown when `@simplewebauthn/browser` cannot be loaded — the expected outcome in
 * host apps that do not ship the dependency. Callers surface it as an ordinary
 * passkey failure.
 */
export class WebAuthnUnavailableError extends Error {
  constructor(public readonly reason?: unknown) {
    super('@simplewebauthn/browser could not be loaded')
    this.name = 'WebAuthnUnavailableError'
  }
}

let modulePromise: Promise<WebAuthnBrowserModule> | null = null
let loadedModule: WebAuthnBrowserModule | null = null

/**
 * Resolves the `@simplewebauthn/browser` module, loading it on first use and reusing
 * it afterwards. Rejects with `WebAuthnUnavailableError` when it cannot be loaded.
 */
export const loadWebAuthnBrowser = (): Promise<WebAuthnBrowserModule> => {
  if (!modulePromise) {
    modulePromise = import(
      /* webpackChunkName: "simplewebauthn-browser" */ '@simplewebauthn/browser'
    ).then(
      (imported) => {
        loadedModule = imported as unknown as WebAuthnBrowserModule
        return loadedModule
      },
      (error) => {
        // Drop the cached rejection so a transient chunk-fetch failure can be
        // retried on the next attempt. A genuinely absent dependency fails again.
        modulePromise = null
        throw new WebAuthnUnavailableError(error)
      }
    )
  }
  return modulePromise
}

/** Runs a WebAuthn authentication ceremony, loading the library if needed. */
export const startWebAuthnAuthentication = (
  options: StartAuthenticationOptions
): Promise<AuthenticationResponseJSON> =>
  loadWebAuthnBrowser().then((webAuthn) => webAuthn.startAuthentication(options))

/** Runs a WebAuthn registration ceremony, loading the library if needed. */
export const startWebAuthnRegistration = (
  options: StartRegistrationOptions
): Promise<RegistrationResponseJSON> =>
  loadWebAuthnBrowser().then((webAuthn) => webAuthn.startRegistration(options))

/**
 * Aborts an in-flight ceremony. Never triggers a load: with the library absent there
 * is no ceremony to cancel. When a load is still in flight the cancellation is queued
 * behind it, so a ceremony started by that same load does not outlive its caller.
 */
export const cancelWebAuthnCeremony = (): void => {
  if (loadedModule) {
    loadedModule.WebAuthnAbortService.cancelCeremony()
    return
  }
  modulePromise
    ?.then((webAuthn) => webAuthn.WebAuthnAbortService.cancelCeremony())
    .catch(() => undefined)
}
